import React, { useState, useEffect } from 'react';
import { usersAPI } from '../../services/api';
import { Card, Button } from '../ui';
import Loading from '../common/Loading';
import UserFormModal from './UserFormModal';
import { formatDate, handleApiError, showToast, showConfirm } from '../../utils/helpers';
import { Plus, Palette, PenSquare, Sparkles } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const UsersListPage = () => {
  const { isRTL, t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [filterRole, setFilterRole] = useState('all'); // all, designer, writer

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await usersAPI.getAll();
      console.log('Users API response:', response.data);
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      console.error('Error response:', err.response?.data);
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm(
      t('confirmDeleteUser'),
      isRTL
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setDeletingId(id);
      await usersAPI.delete(id);
      await fetchUsers();
      showToast.success(t('userDeletedSuccess'));
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setDeletingId(null);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingUser(null);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    fetchUsers();
  };

  const filteredUsers = filterRole === 'all' 
    ? users 
    : users.filter(user => user.role === filterRole);

  const designersCount = users.filter(u => u.role === 'designer').length;
  const writersCount = users.filter(u => u.role === 'writer').length;
  const seoSpecialistsCount = users.filter(u => u.role === 'seo_specialist').length;

  if (loading) {
    return <Loading fullScreen />;
  }

  if (error) {
    return (
      <div className={`text-center py-12 ${isRTL ? 'text-right' : 'text-left'}`}>
        <p className="text-rose-600 mb-4">{error}</p>
        <Button onClick={fetchUsers} variant="secondary">
          {t('tryAgain')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Card className="p-5 bg-gradient-to-r from-sky-50 via-white to-emerald-50 border-slate-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">
              {t('teamMembersTitle') || t('team')}
            </h2>
            <p className="text-slate-600 text-sm mt-1">
              {t('teamMembersSubtitle') || t('manageTeam')}
            </p>
          </div>
          <Button
            variant="primary"
            onClick={handleCreate}
            className="bg-gradient-to-r from-sky-600 to-emerald-500 hover:from-sky-700 hover:to-emerald-600 text-white shadow-lg shadow-sky-500/30"
          >
            <Plus className="w-4 h-4" />
            {t('addTeamMember')}
          </Button>
        </div>

        {/* Summary badges */}
        <div className="mt-4 flex flex-wrap gap-3 text-xs">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 border border-slate-200 text-slate-700">
            👥 {t('allUsersFilter')} {users.length}
          </span>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 border border-slate-200 text-slate-700">
            🎨 {t('designersFilter')} {designersCount}
          </span>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 border border-slate-200 text-slate-700">
            ✍️ {t('writersFilter')} {writersCount}
          </span>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 border border-slate-200 text-slate-700">
            ✨ {t('seoSpecialistsFilter')} {seoSpecialistsCount}
          </span>
        </div>
      </Card>

      {/* Filter Pills */}
      <div className={`flex flex-wrap gap-2 ${isRTL ? 'justify-end' : 'justify-start'}`}>
        <button
          onClick={() => setFilterRole('all')}
          className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
            filterRole === 'all'
              ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
              : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
          }`}
        >
          {t('allUsersFilter')} ({users.length})
        </button>
        <button
          onClick={() => setFilterRole('designer')}
          className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
            filterRole === 'designer'
              ? 'bg-purple-600 text-white border-purple-600 shadow-sm'
              : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
          }`}
        >
          {t('designersFilter')} ({designersCount})
        </button>
        <button
          onClick={() => setFilterRole('writer')}
          className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
            filterRole === 'writer'
              ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
              : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
          }`}
        >
          {t('writersFilter')} ({writersCount})
        </button>
        <button
          onClick={() => setFilterRole('seo_specialist')}
          className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all ${
            filterRole === 'seo_specialist'
              ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
              : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
          }`}
        >
          {t('seoSpecialistsFilter')} ({seoSpecialistsCount})
        </button>
      </div>

      {/* Users Table */}
      {filteredUsers.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <p className="text-gray-500 text-lg mb-4">
              {filterRole === 'all' 
                ? t('noTeamMembersYet') 
                : t('noRoleYet').replace('{role}', t(filterRole === 'designer' ? 'designer' : filterRole === 'writer' ? 'writer' : 'seoSpecialist'))
              }
            </p>
            <Button variant="primary" onClick={handleCreate}>
              {t('addYourFirstTeamMember')}
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th
                    className={`px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${
                      isRTL ? 'text-right' : 'text-left'
                    }`}
                  >
                    {t('nameColumn')}
                  </th>
                  <th
                    className={`px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${
                      isRTL ? 'text-right' : 'text-left'
                    }`}
                  >
                    {t('emailColumn')}
                  </th>
                  <th
                    className={`px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${
                      isRTL ? 'text-right' : 'text-left'
                    }`}
                  >
                    {t('roleColumn')}
                  </th>
                  <th
                    className={`px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${
                      isRTL ? 'text-right' : 'text-left'
                    }`}
                  >
                    {t('statusColumn')}
                  </th>
                  <th
                    className={`px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${
                      isRTL ? 'text-right' : 'text-left'
                    }`}
                  >
                    {t('joinedColumn')}
                  </th>
                  <th
                    className={`px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${
                      isRTL ? 'text-left' : 'text-right'
                    }`}
                  >
                    {t('actionsColumn')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {filteredUsers.map((user) => {
                  const roleColor =
                    user.role === 'designer'
                      ? 'bg-purple-50 text-purple-700 border-purple-100'
                      : user.role === 'writer'
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                      : user.role === 'seo_specialist'
                      ? 'bg-amber-50 text-amber-700 border-amber-100'
                      : 'bg-slate-50 text-slate-700 border-slate-100';

                  const initials =
                    (user.name || user.email || '?')
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase();

                  return (
                    <tr
                      key={user.id}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td
                        className={`px-6 py-4 whitespace-nowrap ${
                          isRTL ? 'text-right' : 'text-left'
                        }`}
                      >
                        <div
                          className={`flex items-center gap-3 ${
                            ''
                          }`}
                        >
                          <div className="h-9 w-9 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center text-xs font-bold">
                            {initials}
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-slate-900">
                              {user.name}
                            </div>
                            <div className="text-xs text-slate-500">
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm text-slate-600 ${
                          isRTL ? 'text-right' : 'text-left'
                        }`}
                      >
                        {user.role === 'admin'
                          ? t('admin') || 'Admin'
                          : user.role === 'designer'
                          ? t('designer') || 'Designer'
                          : user.role === 'writer'
                          ? t('writer') || 'Writer'
                          : user.role === 'seo_specialist'
                          ? t('seoSpecialist') || 'SEO Specialist'
                          : user.role}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap ${
                          isRTL ? 'text-right' : 'text-left'
                        }`}
                      >
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${roleColor}`}
                        >
                          {user.role === 'designer'
                            ? '🎨'
                            : user.role === 'writer'
                            ? '✍️'
                            : user.role === 'seo_specialist'
                            ? '✨'
                            : '👤'}
                          {t(user.role) || user.role}
                        </span>
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap ${
                          isRTL ? 'text-right' : 'text-left'
                        }`}
                      >
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            user.is_active === false
                              ? 'bg-rose-50 text-rose-700 border border-rose-100'
                              : 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                          }`}
                        >
                          <span
                            className={`mr-1 inline-block h-1.5 w-1.5 rounded-full ${
                              user.is_active === false
                                ? 'bg-rose-500'
                                : 'bg-emerald-500'
                            }`}
                          />
                          {user.is_active === false ? t('inactive') : t('active')}
                        </span>
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm text-slate-500 ${
                          isRTL ? 'text-right' : 'text-left'
                        }`}
                      >
                        {formatDate(user.created_at)}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                          isRTL ? 'text-left' : 'text-right'
                        }`}
                      >
                        <div
                          className={`flex items-center gap-2 ${
                            isRTL ? 'justify-start' : 'justify-end'
                          }`}
                        >
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleEdit(user)}
                          >
                            {t('editButton')}
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDelete(user.id)}
                            disabled={deletingId === user.id}
                          >
                            {deletingId === user.id
                              ? t('deleting')
                              : t('deleteButton')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* User Form Modal */}
      {showModal && (
        <UserFormModal
          user={editingUser}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default UsersListPage;

